import { useState, type FormEvent } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { createUser } from '../../lib/userApi';
import type { UserRole } from '../../types/user';

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-mcc-neutral focus:outline-none focus:ring-2 focus:ring-mcc-lightest';

type Message = {
  tone: 'error' | 'success' | 'warning';
  text: string;
};

const messageClass: Record<Message['tone'], string> = {
  error: 'text-red-700',
  success: 'text-green-700',
  warning: 'text-amber-700',
};

export function CreateUserForm(): React.JSX.Element {
  const oidc = useOidcAuth();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const accessToken = oidc.user?.access_token;
    if (!accessToken) {
      setMessage({ tone: 'error', text: 'Your session has expired. Please sign in again.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const email = String(data.get('email') ?? '').trim();
      const result = await createUser(accessToken, {
        name: String(data.get('name') ?? '').trim(),
        email,
        role: String(data.get('role')) as UserRole,
      });
      form.reset();
      setMessage(result.warning
        ? { tone: 'warning', text: result.warning }
        : { tone: 'success', text: `Created ${email}. Cognito emailed the temporary sign-in details.` });
    } catch (reason) {
      setMessage({
        tone: 'error',
        text: reason instanceof Error ? reason.message : 'Unable to create user',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-mcc-darkest">Create user</h2>
      <p className="mt-1 text-sm text-slate-600">
        The user receives an email with a temporary password and completes secure sign-in in Cognito.
      </p>
      <form className="mt-5 grid gap-4 sm:grid-cols-3" onSubmit={submit}>
        <label className="text-sm">
          Full name
          <input className={inputClass} name="name" autoComplete="off" maxLength={160} required />
        </label>
        <label className="text-sm">
          Email
          <input className={inputClass} name="email" type="email" autoComplete="off" maxLength={254} required />
        </label>
        <label className="text-sm">
          Role
          <select className={inputClass} name="role" required>
            <option value="client">Client</option>
            <option value="caregiver">Caregiver</option>
            <option value="operations">Operations</option>
            <option value="system-admin">System admin</option>
          </select>
        </label>
        <button
          className="w-fit rounded-md bg-mcc-neutral px-5 py-2.5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          disabled={saving}
        >
          {saving ? 'Creating…' : 'Create user'}
        </button>
      </form>
      {message && (
        <p className={`mt-4 text-sm ${messageClass[message.tone]}`} role="status">
          {message.text}
        </p>
      )}
    </section>
  );
}
